import React, { memo, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'antd';
import {
  Bold,
  Code,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
} from 'lucide-react';

import { applyMarkdown, TMarkdownAction } from './taskMarkdown';
import { TaskDescription } from './TaskDescription';
import * as M from './TaskModal.styled';

const ICONS: Record<TMarkdownAction, typeof Bold> = {
  bold: Bold,
  italic: Italic,
  bullet: List,
  ordered: ListOrdered,
  link: Link2,
  code: Code,
  quote: Quote,
};

/** Группы кнопок: начертание | списки и цитата | вставка. */
const GROUPS: TMarkdownAction[][] = [
  ['bold', 'italic', 'code'],
  ['bullet', 'ordered', 'quote'],
  ['link'],
];

interface IMarkdownFieldProps {
  value: string;
  rows?: number;
  disabled?: boolean;
  placeholder?: string;
  onChange: (next: string) => void;
  /** Вставка картинки из буфера — окно решает, что с ней делать. */
  onPasteFiles?: (files: File[]) => void;
}

/**
 * Поле описания с панелью разметки и предпросмотром (ТЗ 0066).
 *
 * ОДИН компонент на окно задачи и окно создания. Раздвоенная логика этому
 * модулю уже стоила двух разных цветов у одного признака, а здесь разъехались
 * бы сами кнопки: набранное в одном окне выглядело бы иначе в другом.
 *
 * Формат — Markdown, а не WYSIWYG: разметка видна при наборе, зато хранится
 * ровно то, что человек написал, и показывается это `react-markdown` v6,
 * который сырой HTML не выпускает вовсе.
 */
export const MarkdownField = memo(
  ({
    value,
    rows = 6,
    disabled = false,
    placeholder,
    onChange,
    onPasteFiles,
  }: IMarkdownFieldProps) => {
    const { t } = useTranslation();
    const [preview, setPreview] = useState(false);
    const areaRef = useRef<HTMLTextAreaElement | null>(null);

    const run = useCallback(
      (action: TMarkdownAction) => {
        const node = areaRef.current;
        if (!node) return;
        const next = applyMarkdown(
          { text: value, from: node.selectionStart, to: node.selectionEnd },
          action,
        );
        onChange(next.text);
        // Границы выделения возвращаем в поле СВОИМИ руками и после отрисовки:
        // React перерисует textarea новым value и поставит курсор в конец, а
        // тогда следующая кнопка разметит не то место.
        //
        // `setTimeout`, а НЕ `requestAnimationFrame`: в фоновой вкладке rAF не
        // вызывается вовсе (проверено замером: `visibilityState: hidden` —
        // кадр не пришёл и за полторы секунды). Свернул окно, вернулся, нажал
        // «Жирный» — и курсор уехал бы в конец.
        window.setTimeout(() => {
          const area = areaRef.current;
          if (!area) return;
          area.focus();
          area.setSelectionRange(next.from, next.to);
        }, 0);
      },
      [onChange, value],
    );

    const onPaste = useCallback(
      (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!onPasteFiles) return;
        const files = Array.from(event.clipboardData?.files || []);
        if (!files.length) return;
        // Скриншот из буфера — главный способ приложить картинку. Текст при
        // этом не трогаем: `preventDefault` только когда файлы реально есть.
        event.preventDefault();
        onPasteFiles(files);
      },
      [onPasteFiles],
    );

    return (
      <div>
        <M.MdBar>
          {GROUPS.map((group, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`g${index}`}>
              {index > 0 ? <M.MdSep aria-hidden="true" /> : null}
              {group.map((action) => {
                const Icon = ICONS[action];
                return (
                  <M.MdButton
                    key={action}
                    type="button"
                    disabled={disabled || preview}
                    title={t(`pages.tasks.md.${action}`)}
                    aria-label={t(`pages.tasks.md.${action}`)}
                    // onMouseDown, а не onClick: клик сначала уводит фокус из
                    // textarea, и выделение, по которому мы работаем, теряется.
                    onMouseDown={(event) => {
                      event.preventDefault();
                      run(action);
                    }}
                  >
                    <Icon size={13} strokeWidth={2.2} aria-hidden="true" />
                  </M.MdButton>
                );
              })}
            </React.Fragment>
          ))}
          <M.MdSep aria-hidden="true" />
          <M.MdButton
            type="button"
            aria-pressed={preview}
            title={t('pages.tasks.md.preview')}
            onClick={() => setPreview((on) => !on)}
          >
            {t('pages.tasks.md.preview')}
          </M.MdButton>
          <M.MdHint>{t('pages.tasks.md.hint')}</M.MdHint>
        </M.MdBar>

        {preview ? (
          <M.FileZone>
            {value.trim() ? (
              <TaskDescription value={value} />
            ) : (
              <M.MarkdownEmpty>{t('pages.tasks.md.empty')}</M.MarkdownEmpty>
            )}
          </M.FileZone>
        ) : (
          <Input.TextArea
            ref={(node) => {
              areaRef.current = node
                ? ((node as unknown) as {
                    resizableTextArea?: {
                      textArea: HTMLTextAreaElement;
                    };
                  }).resizableTextArea?.textArea || null
                : null;
            }}
            rows={rows}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            onPaste={onPaste}
            onChange={(event) => onChange(event.target.value)}
          />
        )}
      </div>
    );
  },
);

MarkdownField.displayName = 'MarkdownField';
